import shopifyMapping from '@contextual/shared/shopify_mapping.json' assert { type: 'json' };
import {
  IntegrationQuery,
  IntegrationTypeV2,
  IntegrationV2Query,
  MetaMetadataModel,
  Ga4MetadataModel,
  GoogleAdsMetadataModel,
  MetaCredentialsModel,
  Ga4CredentialsModel,
  KlaviyoCredentialsModel,
  PostscriptCredentialsModel,
  SnapchatCredentialsModel,
  TikTokCredentialsModel,
  GoogleAdsCredentialsModel,
  TikTokMetadataModel,
  SnapchatMetadataModel,
  ConnectionStatusV2,
  SendlaneCredentialsModel
} from '@/gql/graphql';

type DataSource = {
  send_dest?: Record<string, unknown>;
} & Record<string, unknown>;

// Most destination types map to a send_dest key that is just their lowercased
// name; the ones that diverge are listed here.
const SEND_DEST_KEY_OVERRIDES: Partial<Record<IntegrationTypeV2, string>> = {
  [IntegrationTypeV2.Webhook]: 'outbound_webhook'
};

const sendDestKey = (destinationType: IntegrationTypeV2) =>
  SEND_DEST_KEY_OVERRIDES[destinationType] ?? destinationType.toLowerCase();

export const getEventsList = (destinationType: IntegrationTypeV2) => {
  const destKey = sendDestKey(destinationType);
  return Object.entries(shopifyMapping)
    .filter(([_, value]) => {
      return Object.values(value).some(
        (dataSource: DataSource) => dataSource.send_dest && destKey in dataSource.send_dest
      );
    })
    .map(([key, value]) => ({
      value: key,
      label: value.ctx_name
    }));
};

export const getUserSelectedEvents = (data: IntegrationV2Query | null | undefined) => {
  if (!data) return [];
  return data.integrationV2.findFiltersV2
    .map((filter) => (filter.isEnabled ? filter.eventName : null))
    .filter(Boolean) as string[];
};

export const isCustomEventsEnabled = (data: IntegrationV2Query | null | undefined) => {
  if (!data) return false;
  return data.integrationV2.findFiltersV2.some((filter) => !filter.isEnabled);
};

export const useSetupProgress = () => {
  const isSubDomainComplete = () => {
    return localStorage.getItem('subDomainSetup') === 'complete';
  };

  const markSubDomainComplete = () => {
    localStorage.setItem('subDomainSetup', 'complete');
  };

  return {
    isSubDomainComplete,
    markSubDomainComplete
  };
};

export const hasUserGeneratedGoogleAdsCtids = (
  data: IntegrationQuery | null | undefined,
  eventList: { value: string; label: string }[]
) => {
  if (!data) return false;

  // Safely extract ctids or fallback to an empty array if undefined
  const ctids = data.integration.metadata.googleAdsCtids?.ctids ?? [];

  // Check if any user_generated ctids exist
  const hasUserGenerated = ctids.some((ctid) => ctid.ctid.userCreated);
  // Check if there are any events not covered by the ctids
  const hasMissingEvents =
    ctids.length > 0 &&
    eventList.some((event) => !ctids.some((ctid) => ctid.event === event.value));
  return hasUserGenerated || hasMissingEvents;
};

export const getTypedMetadata = (
  data: IntegrationV2Query | null | undefined
):
  | MetaMetadataModel
  | Ga4MetadataModel
  | GoogleAdsMetadataModel
  | TikTokMetadataModel
  | SnapchatMetadataModel
  | null => {
  if (!data?.integrationV2.metadata) return null;

  const metadata = data.integrationV2.metadata;

  switch (data.integrationV2.type) {
    case IntegrationTypeV2.Meta:
      return metadata.__typename === 'MetaMetadataModel' ? metadata : null;
    case IntegrationTypeV2.Ga4:
      return metadata.__typename === 'GA4MetadataModel' ? metadata : null;
    case IntegrationTypeV2.Googleads:
      return metadata.__typename === 'GoogleAdsMetadataModel' ? metadata : null;
    case IntegrationTypeV2.Tiktok:
      return metadata.__typename === 'TikTokMetadataModel' ? metadata : null;
    case IntegrationTypeV2.Snapchat:
      return metadata.__typename === 'SnapchatMetadataModel' ? metadata : null;
    default:
      return null;
  }
};

export const getTypedCredentials = (
  data: IntegrationV2Query | null | undefined
):
  | MetaCredentialsModel
  | Ga4CredentialsModel
  | KlaviyoCredentialsModel
  | PostscriptCredentialsModel
  | SnapchatCredentialsModel
  | TikTokCredentialsModel
  | GoogleAdsCredentialsModel
  | SendlaneCredentialsModel
  | null => {
  if (!data?.integrationV2.credentials) return null;

  const credentials = data.integrationV2.credentials;

  switch (data.integrationV2.type) {
    case IntegrationTypeV2.Meta:
      return credentials.__typename === 'MetaCredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Ga4:
      return credentials.__typename === 'GA4CredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Klaviyo:
      return credentials.__typename === 'KlaviyoCredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Postscript:
      return credentials.__typename === 'PostscriptCredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Snapchat:
      return credentials.__typename === 'SnapchatCredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Tiktok:
      return credentials.__typename === 'TikTokCredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Googleads:
      return credentials.__typename === 'GoogleAdsCredentialsModel' ? credentials : null;
    case IntegrationTypeV2.Sendlane:
      return credentials.__typename === 'SendlaneCredentialsModel' ? credentials : null;
    default:
      return null;
  }
};

export const isMetaMetadata = (
  metadata: ReturnType<typeof getTypedMetadata>
): metadata is MetaMetadataModel => {
  return metadata?.__typename === 'MetaMetadataModel';
};

export const isTikTokMetadata = (
  metadata: ReturnType<typeof getTypedMetadata>
): metadata is TikTokMetadataModel => {
  return metadata?.__typename === 'TikTokMetadataModel';
};

export const isSnapchatMetadata = (
  metadata: ReturnType<typeof getTypedMetadata>
): metadata is SnapchatMetadataModel => {
  return metadata?.__typename === 'SnapchatMetadataModel';
};

export const readableStatus = (value: string) => {
  switch (value) {
    case 'DISCONNECTED':
      return 'Inactive';
    case 'CONNECTED':
      return 'Active';
    case 'ISSUE':
      return 'Issue';
    case 'DRAFT':
      return 'Draft';
    case 'ERROR':
      return 'Error';
    default:
      return 'Unknown';
  }
};

export const LOGO_MAPPING = {
  [IntegrationTypeV2.Meta]: 'meta' as const,
  [IntegrationTypeV2.Ga4]: 'ga4' as const,
  [IntegrationTypeV2.Klaviyo]: 'klaviyo' as const,
  [IntegrationTypeV2.Postscript]: 'postscript' as const,
  [IntegrationTypeV2.Snapchat]: 'snapchat' as const,
  [IntegrationTypeV2.Tiktok]: 'tiktok' as const,
  [IntegrationTypeV2.Googleads]: 'googleads' as const,
  [IntegrationTypeV2.Sendlane]: 'sendlane' as const,
  [IntegrationTypeV2.Attentive]: 'attentive' as const,
  [IntegrationTypeV2.Redo]: 'redo' as const,
  [IntegrationTypeV2.Webhook]: 'webhook' as const,
  [IntegrationTypeV2.Unspecified]: 'unspecified' as const
};

export const readableStatusToStatus = (status: string) => {
  switch (status) {
    case 'active':
      return 'CONNECTED';
    case 'inactive':
      return 'DISCONNECTED';
    case 'CONNECTED':
      return 'Active';
    case 'DISCONNECTED':
      return 'Inactive';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }
};

export const getStatusIndicatorColor = (status: ConnectionStatusV2) => {
  const statusClasses = {
    CONNECTED: 'bg-success-foreground',
    DISCONNECTED: 'bg-foreground',
    DRAFT: 'bg-muted-foreground',
    ERROR: 'bg-destructive-foreground',
    ISSUE: 'bg-destructive-foreground'
  };
  return statusClasses[status];
};
